import { Box, Button, Card, CardContent, CardHeader, Grid, Paper, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { municipalityApiKeyStore } from '../../../../api/masters/municipality-api-key-store';
import { stateApiKeyStore } from '../../../../api/states-api-key-store';
import { FormInput } from '../../../../components/hook-form/form-input.component';
import { FormInputTime } from '../../../../components/hook-form/form-input.time.component';
import { FormSelect } from '../../../../components/hook-form/form-select.component';
import { FormSelectDynamic } from '../../../../components/hook-form/form-select.dynamic.component';
import { ReactHookForm } from '../../../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../../../hooks/useReactHookForm';
import { useSaveData } from '../../../../hooks/useSaveData';
import { Municipality } from '../../../../models/masters/municipality';
import { municipalityFormSchema } from '../municipality-form.schema';
import { ContactsForm } from './contacts.form';
import { FormCheckbox } from '../../../../components/hook-form/form-checkbox.component';
import { FormAutoCompleteSelectDynamic } from '../../../../components/hook-form/form-autocomplete-select.dynamic.component';
import { FormCheckboxGroup } from '../../../../components/hook-form/form-checkbox-groups.component';
import { Label } from '@mui/icons-material';
import FormInputMultiline from '../../../../components/hook-form/form-input-multiline-component';
import { useLoadData } from '../../../../hooks/useLoadData';
import { workOrdersApiKeyStore } from '../../../../api/workorders-api-key-store';
import { ApiResponse } from '../../../../models/api-response';
import { LoadingIndicator } from '../../../../components/loading-indicator.component';
import { NoteComponent } from './note.component';
import { servicesApiKeyStore } from '../../../../api/masters/services-api-key-store';
import { serviceGroupApiKeyStore } from '../../../../api/masters/service-group-api-key-store';
import { GuardedComponent } from '../../../../components/guarded-component';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { useAuthorize } from '../../../../hooks/use-authorize';
import { fileUploadApiKeyStore } from '../../../../api/file-upload-key-store';

const styling = {
  buttonStyle: { width: '130px' },
};

const options = [
  {
    value: 'Active',
    label: 'Active',
    isDefault: true,
  },
  {
    value: 'Inactive',
    label: 'Inactive',
    isDefault: false,
  },
];

interface SaveMunicipality {
  municipality?: Municipality;
}

const SaveMunicipality = ({ municipality }: SaveMunicipality) => {
  
  const navigate = useNavigate();
  const { data: StatusData } = useLoadData<ApiResponse<any>>(
    workOrdersApiKeyStore.getWorkOrderStatus('MunicipalityCoCStatus')
  );
  const { data: PermitNumberData } = useLoadData<ApiResponse<any>>(
    municipalityApiKeyStore.getMunicipalityPermitNumber()
  );
  // const { data: ServiceTypeData } = useLoadData<ApiResponse<any>>(
  //   servicesApiKeyStore.getServiceTypes()
  // );
  // const { data: ServiceGroupData } = useLoadData<ApiResponse<any>>(
  //   serviceGroupApiKeyStore.getServiceGroups()
  // );
  const { data: fileTypeData } = useLoadData<ApiResponse<any>>(
    fileUploadApiKeyStore.getFileTypes('lot')
  );
  const [noteTempEntityID, setNoteTempEntityID] = useState<any>('');
  const [currentTemplateId, setCurrentTemplateId] = useState(municipality?.coCTemplateID || 0);

  const form = useReactHookForm({
    defaultValues: {
      ...municipality,
      municipalityNote: '',
      coCTemplateID: { coCTemplateID: municipality?.coCTemplateID, cocTemplateName: '' },
    },
    yupSchema: municipalityFormSchema,
  });
  const { isLoading, mutateAsync } = useSaveData<Municipality>(
    municipality?.municipalityID
      ? municipalityApiKeyStore.updateMunicipality()
      : municipalityApiKeyStore.createMunicipality()
  );

  const watchValues = form.watch();

  const handleSubmit = async (municipality: any) => {
    await mutateAsync({
      data: {
        ...municipality,
        municipalityContacts: municipality?.municipalityContacts?.map((p: any) => p.contactsID),
        sendInsReport: municipality?.sendInsReport ?? false,
        isMunicipalContact: true,
        municipalityCoCStatus: municipality?.municipalityCoCStatus?.map((data: any) => data?.workOrderStatusID) || [],
        municipalityCoCPermitTypes:
          municipality?.municipalityCoCPermitTypes?.map((data: any) => data?.permitTypeValue) || [],
        coCTemplateID: currentTemplateId,
        coCCoverPageAddress: municipality?.coCCoverPageAddress || '',
        coCRequired: municipality?.coCRequired ?? false,
        noteTempEntityID: noteTempEntityID ?? '',
        buildingOfficialName: municipality?.buildingOfficialName ?? null,
        inspectionReportRequiredForCoC: municipality?.inspectionReportRequiredForCoC ?? false,
        //municipalityCoCServiceGroups: municipality?.municipalityCoCServiceGroups?.map((data: any) => data?.serviceGroupID) || [],
        // municipalityNote: municipality?.municipalityNote
        municipalityCoCAdditionalDocumentTypesForSubmission: municipality?.municipalityCoCAdditionalDocumentTypesForSubmission?.map((data: any) => data?.fileTypeID) || [],
        municipalityCoCSummaryReportTypes: municipality?.municipalityCoCSummaryReportTypes?.map((data: any) => data?.workOrderStatusID) || [],
      },
    }).then((data) => navigate(`/masters/municipality/details/${data?.data?.municipalityID}`));

  };

  const onCancel = () => {
    if (municipality?.municipalityID) {
      navigate(`/masters/municipality/details/${municipality?.municipalityID}`);
    } else {
      navigate('/masters/municipality');
    }
  };

  const { data: templateDetails, isFetching: isLoadingTemplateDetails } = useLoadData<ApiResponse<any>>(
    municipalityApiKeyStore.getDetailsByTemplateID(municipality?.municipalityID, currentTemplateId)
  );

  useEffect(() => {
    if (templateDetails) {
      form.setValue('municipalityCoCStatus', templateDetails?.data?.municipalityCoCStatus || []);
      form.setValue('municipalityCoCPermitTypes', templateDetails?.data?.municipalityCoCPermitTypes || []);
      form.setValue('coCCoverPageAddress', templateDetails?.data?.coCCoverPageAddress || '');
      form.setValue('buildingOfficialName', templateDetails?.data?.buildingOfficialName || '');
      //form.setValue('municipalityCoCServiceGroups', templateDetails?.data?.municipalityCoCServiceGroups || []);
      form.setValue('municipalityCoCAdditionalDocumentTypesForSubmission', templateDetails?.data?.municipalityCoCAdditionalDocumentTypesForSubmission || []);
      form.setValue('municipalityCoCSummaryReportTypes', templateDetails?.data?.municipalityCoCSummaryReportTypes || []);
    }
  }, [templateDetails]);

  const handleMunicipalityTemplateChange = (data: any) => {
    form.setValue('municipalityCoCStatus', []);
    form.setValue('municipalityCoCPermitTypes', []);
    form.setValue('coCCoverPageAddress', '');
    form.setValue('buildingOfficialName', '');
    //form.setValue('municipalityCoCServiceGroups', []);
    form.setValue('municipalityCoCAdditionalDocumentTypesForSubmission', []);
    setCurrentTemplateId(data?.coCTemplateID || 0);
    form.setValue('municipalityCoCSummaryReportTypes', []);
  };

  const handleNoteTempID = (noteTempEntityID: any) => {
    setNoteTempEntityID(noteTempEntityID);
  }

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <div>
      {' '}
      <ReactHookForm formName="EDIT_CUSTOMER_FORM" onSubmit={handleSubmit} formContextProps={form}>
        <Grid container spacing={2} style={{ paddingBottom: '15px', paddingTop: '15px' }}>
          <Grid item xl={6} lg={6} sm={6} xs={6}>
            <FormSelectDynamic
              name="stateID"
              label="State"
              getQueryKeyConfig={() => stateApiKeyStore.getStates()}
              optionsValueKey="stateID"
              optionsLabelKeys={['stateName']}
            />
          </Grid>
          <Grid item xl={6} lg={6} sm={6} xs={6}>
            <FormInput label="Municipality Name" name="muncipalityName" maxLength={150} multiline={false} autoFocus />
          </Grid>
          <Grid item xl={3} lg={3} sm={3} xs={6}>
            <FormInput
              label="Code"
              name="municipalityCode"
              maxLength={150}
              multiline={false}
              disabled={!!municipality?.municipalityID}
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={3} xs={6}>
            <FormInput name="contactEmail" label="Contact Email" />
          </Grid>
          <Grid item xl={3} lg={3} sm={6} xs={6}>
            <FormInputTime name="cutOffTime" label="Cut-off Time" />
          </Grid>
          <Grid item xl={3} lg={3} sm={6} xs={6}>
            <FormSelect label="Status" name="status" options={options} />
          </Grid>

          <Grid item xl={3} lg={3} sm={6} xs={6}>
            <FormCheckbox label="Inspection Report Required" name="sendInsReport" />
          </Grid>
         
            <Grid item xl={3} lg={3} sm={6} xs={6}>
              <FormCheckbox label="CoC Required" name="coCRequired" />
            </Grid>
         
            <Grid item xl={3} lg={3} sm={6} xs={6}>
              <FormCheckbox label="Inspection Report Required For CoC" name="inspectionReportRequiredForCoC" />
            </Grid>
          
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_NOTE]}>
            <Grid item xl={12} lg={12} sm={12} xs={12}>
              <NoteComponent
                name="municipalityNotes"
                setNoteTempID={handleNoteTempID}
                noteRequestArgs={{ municipalityID: municipality?.municipalityID }}
              />
            </Grid>
          </GuardedComponent>
        </Grid>
        <ContactsForm name="municipalityContacts" label="Contacts" />
        {isLoadingTemplateDetails && <LoadingIndicator isLoading={isLoadingTemplateDetails} />}
        {!!form.watch('coCRequired') && (
          <Grid container spacing={2} style={{ paddingBottom: '15px', paddingTop: '15px' }}>
            <Grid item xl={6} lg={6} sm={6} xs={6}>
              <FormAutoCompleteSelectDynamic
                name="coCTemplateID"
                label="Template"
                onChange={handleMunicipalityTemplateChange}
                getQueryKeyConfig={() => municipalityApiKeyStore.getMunicipalityTemplate()}
                optionsValueKey="coCTemplateID"
                optionsLabelKey="cocTemplateName"
              />
            </Grid>

            {!!form.watch('coCTemplateID')?.coCTemplateID && !isLoadingTemplateDetails && (
              <>
                <Grid item xl={6} lg={6} sm={6} xs={6}>
                  <FormInput
                    label="Building Department Title"
                    name="buildingOfficialName"
                  />
                </Grid>
                <Grid item xl={6} lg={6} sm={6} xs={6}>
                  <FormInputMultiline name="coCCoverPageAddress" label="CoC Cover Page Address" multiline size={5.0} />
                </Grid>
                <Grid item xl={6} lg={6} sm={6} xs={6}>
                  <div className="FormCheckboxGroupContainer">
                    <label className="FormCheckboxGroupLegendLabel">Report Types Included in CoC</label>
                    <FormCheckboxGroup
                      name="municipalityCoCStatus"
                      options={StatusData?.data || []}
                      labelKey="workOrderStatus"
                      valueKey="workOrderStatusID"
                    />
                  </div>
                </Grid>
                 <Grid item xl={6} lg={6} sm={6} xs={6}>
                  <div className="FormCheckboxGroupContainer">
                    <label className="FormCheckboxGroupLegendLabel">Report Types Included in Summary</label>
                    <FormCheckboxGroup
                      name="municipalityCoCSummaryReportTypes"
                      options={StatusData?.data || []}
                      labelKey="workOrderStatus"
                      valueKey="workOrderStatusID"
                    />
                  </div>
                </Grid>
                <Grid item xl={6} lg={6} sm={6} xs={6}>
                  <div className="FormCheckboxGroupContainer">
                    <label className="FormCheckboxGroupLegendLabel">Permit Types</label>
                    <FormCheckboxGroup
                      name="municipalityCoCPermitTypes"
                      columns={3}
                      options={PermitNumberData?.data || []}
                      labelKey="permitType"
                      valueKey="permitTypeValue"
                      xl={3}
                      lg={4}
                      md={6}
                      sm={12}
                      xs={12}
                    />
                  </div>
                </Grid>
               
                 <Grid item xl={6} lg={6} sm={6} xs={6}>
                    <div className="FormCheckboxGroupContainer">
                      <label className="FormCheckboxGroupLegendLabel">Additional Documents to be Included in Submission</label>
                      <FormCheckboxGroup
                        name="municipalityCoCAdditionalDocumentTypesForSubmission"
                        options={fileTypeData?.data || []}
                        labelKey="fileTypeName"
                        valueKey="fileTypeID"
                      />
                    </div>
                  </Grid>
                
                  {/* <Grid item xl={6} lg={6} sm={6} xs={6}>
                    <div className="FormCheckboxGroupContainer">
                      <label className="FormCheckboxGroupLegendLabel">Service Group</label>
                      <FormCheckboxGroup
                        name="municipalityCoCServiceGroups"
                        options={ServiceGroupData?.data || []}
                        labelKey="serviceGroupName"
                        valueKey="serviceGroupID"
                      />
                    </div>
                  </Grid> */}
               
              </>
            )}
          </Grid>
        )}
        <Grid container justifyContent="space-between" spacing={1} style={{ paddingTop: '15px' }}>
          <Grid item>
            <Button variant="outlined" style={styling.buttonStyle} onClick={onCancel}>
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              type={'submit'}
              disabled={isLoading || isReadOnlyPermission}
              variant="contained"
              disableElevation={true}
              style={styling.buttonStyle}
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </ReactHookForm>
    </div>
  );
};

export default SaveMunicipality;
